import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { chain } from 'lodash';
import { useField } from 'react-final-form';
import { setDefaultValues } from '../../../../../../../utils/utils';
import {
  getVolumeNumber,
  getNextCopyNumberForVolume,
  recalculateCopyNumbers,
} from '../utils/volumeUtils';
import { useBarcodeSession } from '../../../../../../../contexts/BarcodeSessionContext';

const useVolumeManagement = ({
  fields,
  template,
  holdingsRecordId,
  holdingLocatitionId,
  classificationNumber,
  authorNumber,
  setBarcodeValue,
  setSelectedItems,
  setSelectAll,
  resetVolumeStates,
  createUndoAction,
  setShowCreateSingleItemModal,
  setSelectedVolumeForModal,
  volumeAccordionStatus,
  setVolumeAccordionStatus,
}) => {
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const { addBarcodes, removeBarcodes } = useBarcodeSession();
  const instanceStatisticalCodeIdsField = useField('instanceData.statisticalCodeIds');
  const instanceStatisticalCodeIds = instanceStatisticalCodeIdsField?.input?.value || [];

  // ✅ Enhanced volume management functions with proper barcode sequencing
  const handleAddVolume = (volumesToCreate, barcodeSettings = null) => {
    const newBarcodes = []; // Track barcodes for session management

    volumesToCreate.forEach(({ volume, items }) => {
      // Use items from AddVolumeModal if available, otherwise fallback to old logic
      if (items && items.length > 0) {
        // New logic: Use pre-created items from AddVolumeModal
        items.forEach((itemData, index) => {
          const itemId = uuidv4();
          const copyNumber = index + 1;

          const newItem = {
            ...setDefaultValues(template?.templateFields?.items),
            id: itemId,
            holdingsRecordId,
            permanentLocation: itemData.permanentLocation, // Use from AddVolumeModal
            barcode: itemData.barcode || '',
            copyNumber,
            itemLevelCallNumber: `${classificationNumber}${authorNumber}`,
            volume,
            ...(instanceStatisticalCodeIds.length && { itemStatisticalCodeIds: instanceStatisticalCodeIds }),
          };

          fields.push(newItem);

          // Track barcode for session management
          if (newItem.barcode && newItem.barcode.trim() !== '') {
            newBarcodes.push(newItem.barcode.trim());
          }
        });
      } else {
        // Fallback: Old logic for backward compatibility
        const count = volumesToCreate.count || 1;
        let currentBarcodeIndex = fields.value ? fields.value.length : 0;
        
        for (let i = 0; i < count; i++) {
          const itemId = uuidv4();
          const copyNumber = i + 1;

          let barcode = '';
          if (barcodeSettings && barcodeSettings.barcodes && barcodeSettings.barcodes.length > i) {
            barcode = barcodeSettings.barcodes[i] || '';
          } else {
            barcode = setBarcodeValue(holdingLocatitionId, currentBarcodeIndex + 1);
          }

          const newItem = {
            ...setDefaultValues(template?.templateFields?.items),
            id: itemId,
            holdingsRecordId,
            permanentLocation: { id: holdingLocatitionId },
            barcode,
            copyNumber,
            itemLevelCallNumber: `${classificationNumber}${authorNumber}`,
            volume,
            ...(instanceStatisticalCodeIds.length && { itemStatisticalCodeIds: instanceStatisticalCodeIds }),
          };

          fields.push(newItem);
          currentBarcodeIndex++;

          if (barcode && barcode.trim() !== '') {
            newBarcodes.push(barcode.trim());
          }
        }
      }
    });

    // Add all new barcodes to session tracking
    if (newBarcodes.length > 0) {
      addBarcodes(newBarcodes);
    }
  };

  // ✅ Enhanced handleDeleteVolume with undo support
  const handleDeleteVolume = (volume) => {
    const fieldsArray = fields.value || [];
    const itemsToDelete = [];
    const deletedItemsData = [];

    // Collect items to delete with their indices
    fieldsArray.forEach((item, index) => {
      if ((item.volume || 'No_Volume') === volume) {
        itemsToDelete.unshift(index);
        deletedItemsData.push({ item: { ...item }, index });
      }
    });

    // Extract barcodes from deleted items for session cleanup
    const deletedBarcodes = deletedItemsData
      .map(({ item }) => item.barcode)
      .filter(Boolean); // Filter out empty/null barcodes

    // Create undo action before deletion
    createUndoAction(
      'DELETE_VOLUME',
      {
        volume,
        deletedItems: deletedItemsData,
        deletedBarcodes, // Store barcodes for undo restoration
      },
      'undoSystem.actions.deleteVolume',
    );

    // Delete items from the end to avoid index shifting
    itemsToDelete.forEach((index) => {
      if (fields.length > index) {
        fields.remove(index);
      }
    });

    // Remove deleted barcodes from session tracking
    if (deletedBarcodes.length > 0) {
      removeBarcodes(deletedBarcodes);
    }

    // Volume deleted successfully

    // Reset volume states
    resetVolumeStates();
  };

  // ✅ Enhanced handleDeleteAllVolumes with undo support
  const handleDeleteAllVolumes = () => {
    setIsDeletingAll(true);

    // Store all current data for undo
    const allItems = [...(fields.value || [])];

    // Extract barcodes from all items for session cleanup
    const allDeletedBarcodes = allItems
      .map(item => item.barcode)
      .filter(Boolean); // Filter out empty/null barcodes

    // Create undo action before deletion
    createUndoAction(
      'DELETE_ALL_VOLUMES',
      {
        allItems,
        deletedBarcodes: allDeletedBarcodes, // Store barcodes for undo restoration
      },
      'undoSystem.actions.deleteAllVolumes',
    );

    setSelectedItems([]);
    setSelectAll(false);
    resetVolumeStates();

    requestAnimationFrame(() => {
      const itemCount = fields.length;
      const batchSize = 50;
      let currentIndex = itemCount - 1;

      const deleteBatch = () => {
        const endIndex = Math.max(currentIndex - batchSize + 1, 0);

        for (let i = currentIndex; i >= endIndex; i--) {
          if (fields.length > 0) {
            fields.remove(i);
          }
        }

        currentIndex = endIndex - 1;

        if (currentIndex >= 0 && fields.length > 0) {
          setTimeout(deleteBatch, 0);
        } else {
          // Remove all deleted barcodes from session tracking when deletion is complete
          if (allDeletedBarcodes.length > 0) {
            removeBarcodes(allDeletedBarcodes);
          }
          setIsDeletingAll(false);
        }
      };

      if (itemCount > 0) {
        deleteBatch();
      } else {
        // Remove all deleted barcodes from session tracking (for empty case)
        if (allDeletedBarcodes.length > 0) {
          removeBarcodes(allDeletedBarcodes);
        }
        setIsDeletingAll(false);
      }
    });
  };

  // ✅ Duplicate volume with proper barcode indexing
  const handleDuplicateVolume = (sourceVolume, newVolumeString, barcodeSettings = null) => {
    const fieldsArray = fields.value || [];
    const sourceItems = fieldsArray.filter(
      (item) => (item.volume || 'No_Volume') === sourceVolume,
    );

    // ✅ Fixed: Use proper barcode indexing for duplicated items
    let currentBarcodeIndex = fieldsArray.length;
    const newBarcodes = []; // Track barcodes for session management

    sourceItems.forEach((sourceItem, index) => {
      const itemId = uuidv4();
      const copyNumber = index + 1; // Sequential copy numbers starting from 1

      // Determine barcode based on provided settings
      let barcode = '';
      if (barcodeSettings && barcodeSettings.enabled && barcodeSettings.barcodes && barcodeSettings.barcodes.length > index) {
        // Use provided sequential barcode from SequentialBarcodeSection
        barcode = barcodeSettings.barcodes[index] || '';
      } else {
        // Fall back to automatic generation
        barcode = setBarcodeValue(holdingLocatitionId, currentBarcodeIndex + 1);
      }

      const newItem = {
        ...sourceItem,
        id: itemId,
        barcode,
        copyNumber,
        volume: newVolumeString,
      };

      fields.push(newItem);
      currentBarcodeIndex++; // ✅ Increment after each item

      // Track barcode for session management
      if (barcode && barcode.trim() !== '') {
        newBarcodes.push(barcode.trim());
      }
    });

    // Add all new barcodes to session tracking
    if (newBarcodes.length > 0) {
      addBarcodes(newBarcodes);
    }

    // Volume duplicated successfully
  };

  // Add new item to specific volume - Show modal instead of direct creation
  const handleAddItemToVolume = (volume) => {
    // Set the selected volume for modal and show the modal
    setSelectedVolumeForModal(volume);
    setShowCreateSingleItemModal(true);
  };


  // ✅ Enhanced resetVolumeItemNumbers with undo support
  const resetVolumeItemNumbers = (volume, newVolumeString) => {
    if (!fields?.value) return;

    const fieldsArray = fields.value;

    // Store original data for undo
    const volumeItemsWithIndex = fieldsArray
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => (item.volume || 'No_Volume') === volume)
      .sort((a, b) => a.index - b.index);

    const originalItems = volumeItemsWithIndex.map(({ item, index }) => ({
      item: { ...item },
      index,
    }));

    // Create undo action before reset
    createUndoAction(
      'RESET_VOLUME',
      {
        volume,
        newVolumeString,
        originalItems,
      },
      'undoSystem.actions.resetVolume',
    );

    // Apply the reset
    volumeItemsWithIndex.forEach(({ item, index }, copyIndex) => {
      const newCopyNumber = copyIndex + 1;
      const updatedItem = {
        ...item,
        copyNumber: newCopyNumber,
        volume: newVolumeString,
      };

      fields.update(index, updatedItem);
    });
  };

  // ✅ Toggle all volume accordions with new icon style
  const toggleAllVolumeAccordions = () => {
    const fieldsArray = fields?.value || [];
    if (!fieldsArray.length) return;

    const groupedData = chain(fieldsArray)
      .sortBy((item) => getVolumeNumber(item.volume))
      .groupBy((item) => item.volume || 'No_Volume')
      .value();

    const volumeKeys = Object.keys(groupedData).map(
      (vol) => `accordion-volume-${vol}`,
    );
    const allExpanded = volumeKeys.every((key) => volumeAccordionStatus[key]);

    setVolumeAccordionStatus((current) => {
      const newStatus = { ...current };
      volumeKeys.forEach((key) => {
        newStatus[key] = !allExpanded;
      });
      return newStatus;
    });
  };

  return {
    // State
    isDeletingAll,

    // Volume Management Functions
    handleAddVolume,
    handleDeleteVolume,
    handleDeleteAllVolumes,
    handleDuplicateVolume,
    handleAddItemToVolume,
    resetVolumeItemNumbers,
    toggleAllVolumeAccordions,
  };
};

export default useVolumeManagement;
